<template>
    <div
        class="at1024:rounded-lg at1024:border at1024:border-gray-300 at1024:p-4 at1200:p-8"
    >
        <h3 class="sr-only">Book this event</h3>
        <div
            class="mx-auto max-w-[500px] space-y-4 at1024:max-w-full at1200:space-y-6"
        >
            <div v-if="!product.price.isFree" class="flex flex-row">
                <event-price
                    :price="product.price"
                    class="grow text-2xl font-semibold text-black at1024:text-xl at1200:text-2xl"
                ></event-price>

                <reviews-badge
                    v-if="product.ratingType !== 'host'"
                    :rating-score="product.ratingScore || 0"
                    :rating-count="product.ratingCount"
                    :hide-rating-count="true"
                ></reviews-badge>
            </div>
            <div
                v-if="
                    product.published && (product.isSalable || product.variants)
                "
            >
                <checkout-form
                    ref="variantMenu"
                    @qty-changed="changeQty"
                    @variant-selected="variantSelected"
                ></checkout-form>

                <table
                    v-if="!product.price.isFree"
                    class="mt-6 w-full text-sm at768:w-6/12 at1024:w-full"
                >
                    <tbody>
                        <tr v-if="product.price.base">
                            <td
                                class="pr-4 pt-2"
                                :class="{
                                    'pb-4':
                                        !product.price.perPerson &&
                                        !product.hasShipments,
                                }"
                            >
                                Event price
                            </td>
                            <td
                                class="pt-2 text-right"
                                :class="{
                                    'pb-4':
                                        !product.price.perPerson &&
                                        !product.hasShipments,
                                }"
                            >
                                {{ formatCurrency(product.price.base) }}
                            </td>
                        </tr>
                        <tr v-if="product.price.perPerson">
                            <td
                                class="pr-4 pt-2"
                                :class="{ 'pb-4': !product.hasShipments }"
                            >
                                {{ participantCount }} people x
                                {{ formatCurrency(product.price.perPerson) }}
                                pp
                            </td>
                            <td
                                class="pt-2 text-right"
                                :class="{ 'pb-4': !product.hasShipments }"
                            >
                                {{
                                    formatCurrency(
                                        product.price.perPerson *
                                            participantCount
                                    )
                                }}
                            </td>
                        </tr>

                        <template v-if="cart.items.length">
                            <tr data-testid="cart-addon-line-items-header">
                                <td colspan="2" class="pt-4 font-semibold">
                                    ADDONS
                                </td>
                            </tr>
                            <tr
                                v-for="(item, idx) in cart.items"
                                class="cart-addon-line-item"
                                :data-testid="`cart-addon-line-item-${idx}`"
                            >
                                <td class="flex items-center space-x-2 pt-2">
                                    <span class="max-w-72">
                                        {{ item.listing.name }}
                                        <template v-if="item.qty > 1">
                                            ({{ item.qty }} x
                                            {{
                                                formatCurrency(
                                                    item.listing.price.base
                                                )
                                            }})
                                        </template>
                                    </span>
                                    <a
                                        @click.prevent="
                                            removeFromCart(item.listing.uuid)
                                        "
                                        class="shrink-0 text-xs font-semibold underline underline-offset-2"
                                        href="#"
                                        title="Remove item"
                                        data-testid="remove-addon"
                                    >
                                        <x-circle-icon
                                            alt="remove"
                                            class="w-5 text-orange"
                                        ></x-circle-icon>
                                    </a>
                                </td>
                                <td class="pt-2 text-right">
                                    {{ formatCurrency(item.totals.subtotal) }}
                                </td>
                            </tr>
                        </template>

                        <template v-if="shippingTotal > 0">
                            <tr data-testid="cart-shipping-line-items-header">
                                <td colspan="2" class="pt-4 font-semibold">
                                    SHIPPING
                                </td>
                            </tr>
                            <tr
                                v-for="(item, idx) in shippingItems"
                                class="cart-shipping-line-item"
                                :data-testid="`cart-shipping-line-item-${idx}`"
                            >
                                <td class="pr-4 pt-2">
                                    {{ item.label }} &ndash; shipping
                                    <span v-if="item.qty > 1">
                                        ({{ item.qty }} x
                                        {{ formatCurrency(item.price) }})</span
                                    >
                                </td>
                                <td class="pt-2 text-right">
                                    {{ formatCurrency(item.total) }}
                                </td>
                            </tr>
                        </template>

                        <tr>
                            <td colspan="2" class="">
                                <hr class="mt-4 h-px border-0 bg-gray-300" />
                            </td>
                        </tr>
                        <tr class="text-lg">
                            <td class="pr-4 pt-4 font-semibold">
                                Total before taxes (USD)
                            </td>
                            <td class="pt-4 text-right font-semibold">
                                {{ formatCurrency(grandTotal) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else>
                <p>Unfortunately, this event is no longer available.</p>
                <a
                    v-if="product.primaryCategoryUrl"
                    :href="product.primaryCategoryUrl"
                    class="btn-orange button-custom-branded mt-4 block w-full"
                >
                    See similar events
                </a>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useListingCart } from '@/composables/useListingCart';
import { formatCurrency } from '@/helpers/priceFormatters';
import { XCircleIcon } from '@heroicons/vue/solid';
import EventPrice from 'components/Global/EventPrice';
import ReviewsBadge from 'components/Global/ReviewsBadge.vue';
import CheckoutForm from 'components/ListingDetail/CheckoutForm.vue';
import { computed, inject, ref, shallowRef } from 'vue';

const product = inject('product');
const participantCount = ref(product.participants.min);
const selectedVariant = shallowRef({});

const {
    cart,
    setParticipantCount: setCartParticipantCount,
    removeFromCart,
} = useListingCart(product.uuid);
setCartParticipantCount(participantCount.value);

const emit = defineEmits(['variant-selected']);

const shippingMethod = computed(() => {
    let method = 'Standard shipping';
    if (product.shippingMethod) {
        method = product.shippingMethod.includes(' shipping')
            ? product.shippingMethod
            : `${product.shippingMethod} shipping`;
    }
    if (product.price.shipping > 0) {
        method += ` x ${formatCurrency(product.price.shipping)} pp`;
    }
    return method;
});

function variantSelected(variant) {
    selectedVariant.value = variant;
    emit('variant-selected', variant);
}

const grandTotal = computed(() => {
    return (
        product.price.base +
        cart.value.totals.subtotal +
        product.price.perPerson * participantCount.value +
        shippingTotal.value
    );
});

const shippingTotal = computed(() => {
    return (
        product.price.shipping * participantCount.value +
        cart.value.totals.shipping_total
    );
});

const shippingItems = computed(() => {
    const items = [];
    if (product.price.shipping > 0) {
        items.push({
            qty: participantCount.value,
            price: product.price.shipping,
            total: product.price.shipping * participantCount.value,
            label: shippingMethod.value,
        });
    }
    cart.value.items.forEach((entry) => {
        if (entry.totals.shipping_total > 0) {
            items.push({
                qty: entry.qty,
                price: entry.listing.price.shipping,
                total: entry.totals.shipping_total,
                label: entry.listing.name,
            });
        }
    });

    return items;
});

const changeQty = (qty) => {
    participantCount.value = qty;
    setCartParticipantCount(qty);
};
</script>
